import api from './api';
import axios from 'axios';
import { getConfig } from '@/utils/config';
import { buildPwaLogoUrl } from '@/utils/pwa';
import { dispatchStorageEvent } from '@/utils/auth';

/** Logo served from public/upload (same path after build; backend can replace file without rebuild) */
function getPublicUploadLogo() {
  return `${import.meta.env.BASE_URL}${buildPwaLogoUrl()}`.replace(/\/{2,}/g, '/');
}

/**
 * Public API instance for endpoints that don't require authentication
 * This instance doesn't add auth tokens to requests
 */
const getPublicApiBaseURL = () => getConfig('VITE_API_BASE_URL', '');

const publicApi = axios.create({
  baseURL: getPublicApiBaseURL(),
  withCredentials: true,
});

// Update publicApi baseURL when config is loaded
export function updatePublicApiBaseURL() {
  const newBaseURL = getPublicApiBaseURL();
  if (newBaseURL) {
    publicApi.defaults.baseURL = newBaseURL;
  }
}

// Add request interceptor for public API (only language header, no auth token)
publicApi.interceptors.request.use(
  (config) => {
    config.headers = {
      ...config.headers,
      'Accept-Language': 'fa-IR',
    };
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Get static logo URL from runtime config
 * Reads base URL from VITE_API_BASE_URL and appends /client/datafile/downloadlogo
 * Adds cache-busting query parameter to prevent browser caching
 */
export function getStaticLogoUrl() {
  const baseUrl = getConfig('VITE_API_BASE_URL', 'https://test.aryazar.ir/api/v1');
  // Ensure base URL doesn't end with slash
  const cleanBaseUrl = baseUrl.replace(/\/$/, '');
  // Add timestamp query parameter to prevent caching
  const timestamp = Date.now(); 
  return `${cleanBaseUrl}/client/datafile/downloadlogo?t=${timestamp}`;
}

/**
 * Build logo URL from code or file path
 * If logo is already a full URL, return it as is
 * If it's a Windows file path, convert it to a web URL
 * Otherwise, build the download URL using the code
 */
export function buildLogoUrl(logoCode) {
  if (!logoCode) return '';

  // If it's already a full URL, return it as is
  if (logoCode.startsWith('http://') || logoCode.startsWith('https://') || logoCode.startsWith('data:')) {
    return logoCode;
  }

  // If it's a Windows file path (e.g., C:\inetpub\wwwroot\...), convert to URL
  if (logoCode.includes('\\') || logoCode.startsWith('C:')) {
    // Extract the file path after wwwroot
    const wwwrootIndex = logoCode.indexOf('wwwroot');
    if (wwwrootIndex !== -1) {
      const relativePath = logoCode.substring(wwwrootIndex + 'wwwroot'.length).replace(/\\/g, '/');
      const baseUrl = getConfig('VITE_FILE_BASE_URL', 'https://test.aryazar.ir');
      return `${baseUrl}${relativePath}`;
    }
    // If wwwroot not found, try to extract upload folder path
    const uploadIndex = logoCode.indexOf('upload');
    if (uploadIndex !== -1) {
      const relativePath = '/' + logoCode.substring(uploadIndex).replace(/\\/g, '/');
      const baseUrl = getConfig('VITE_FILE_BASE_URL', 'https://test.aryazar.ir');
      return `${baseUrl}${relativePath}`;
    }
  }

  // Build the download URL using code (for legacy support)
  const baseUrl = getConfig('VITE_FILE_BASE_URL', 'https://test.aryazar.ir');
  const encodedCode = encodeURIComponent(logoCode);
  return `${baseUrl}/api/v1/client/datafile/downloadfile?Code=${encodedCode}`;
}

/**
 * Get admin message (accessible to all authenticated users)
 */
export async function getAdminMessage() {
  const res = await api.get('/settings/admin-message');
  return res.data;
}

/**
 * Update admin message (admin only)
 */
export async function updateAdminMessage(message) {
  const res = await api.put('/settings/admin-message', { message });
  return res.data;
}

// Cache for ongoing requests to prevent duplicate API calls
let ongoingRequest = null;

/**
 * Get public settings
 * If user has token: calls both /client/tradingappinfo/appinfo and /trading/person/info
 * If no token: calls only /client/tradingappinfo/appinfo (public endpoint)
 * Caches response in localStorage for faster initial load
 * Uses promise cache to prevent duplicate concurrent requests
 */
export async function getAppInfoSettings() {
  const STORAGE_KEY = 'app_public_settings';
  const token = localStorage.getItem('auth_token');
  const hasToken = !!token;

  // If there's an ongoing request, return the same promise
  if (ongoingRequest) {
    return ongoingRequest;
  }

  // Try to read from localStorage first (for fast initial load)
  let cachedData = null;
  try {
    const cached = localStorage.getItem(STORAGE_KEY);
    if (cached) {
      cachedData = JSON.parse(cached);
    }
  } catch (err) {
    console.warn('Failed to read cached settings from localStorage:', err);
  }

  // Create the request promise
  ongoingRequest = (async () => {
    try {
      let apiData;

      if (hasToken) {
        // If user has token, call both endpoints
        const [publicRes, authRes] = await Promise.all([
          publicApi.get('/client/tradingappinfo/appinfo'),
          api.get('/trading/person/info')
        ]);

        // Use authenticated endpoint data (includes user info and app settings)
        // But we still call public endpoint as requested
        apiData = authRes.data?.data || authRes.data;
      } else {
        // Otherwise use only public endpoint (no auth required)
        const res = await publicApi.get('/client/tradingappinfo/appinfo');
        apiData = res.data?.data || res.data;
      }

      // Store full API response in localStorage
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(apiData));
        dispatchStorageEvent(STORAGE_KEY, apiData);
      } catch (err) {
        console.warn('Failed to save settings to localStorage:', err);
      }

      // Map new API response fields to old field names for backward compatibility
      const result = {
        app_name: apiData?.applicationName || '',
        app_logo: getPublicUploadLogo() || getStaticLogoUrl(), // Static logo URL from env - not read from API
        phone_number_1: apiData?.phoneNumber1 || '',
        phone_number_2: apiData?.phoneNumber2 || '',
        app_url: apiData?.webSiteAddress || '',
        whatsapp_number: apiData?.whatsAppNumber || '',
        instagram_id: apiData?.instagramAccount || '',
        telegram_number: apiData?.telegramNumber || '',
        address: apiData?.address || '',
        pending_order_rejection_seconds: apiData?.rejectTime?.toString() || '',
        admin_sms_phone: apiData?.adminPhoneNumber || '',
        trading_start_time: apiData?.startTimeTrade || '',
        manager_message: apiData?.managerMessage || '',
        lafzIsEnable: apiData?.lafzIsEnable === true,
      };

      // Clear ongoing request after completion
      ongoingRequest = null;
      return result;
    } catch (err) {
      // Clear ongoing request on error
      ongoingRequest = null;

      // If API call fails and we have cached data, return cached data
      if (cachedData) {
        console.warn('API call failed, using cached settings:', err);
        return {
          app_name: cachedData?.applicationName || '',
          app_logo: getPublicUploadLogo() || getStaticLogoUrl(), // Static logo URL from env - not read from API
          phone_number_1: cachedData?.phoneNumber1 || '',
          phone_number_2: cachedData?.phoneNumber2 || '',
          app_url: cachedData?.webSiteAddress || '',
          whatsapp_number: cachedData?.whatsAppNumber || '',
          instagram_id: cachedData?.instagramAccount || '',
          telegram_number: cachedData?.telegramNumber || '',
          address: cachedData?.address || '',
          pending_order_rejection_seconds: cachedData?.rejectTime?.toString() || '',
          admin_sms_phone: cachedData?.adminPhoneNumber || '',
          trading_start_time: cachedData?.startTimeTrade || '',
          manager_message: cachedData?.managerMessage || '',
          lafzIsEnable: cachedData?.lafzIsEnable === true,
        };
      }
      // If no cached data and API fails, throw the error
      throw err;
    }
  })();

  return ongoingRequest;
}

/**
 * Get all settings (admin only)
 * Uses new API endpoint: /trading/person/info
 * Also updates localStorage cache
 */
export async function getUserInfoSettings() {
  const STORAGE_KEY = 'app_public_settings';

  try {
    // Always fetch fresh data from API
    const res = await api.get('/trading/person/info');
    // API response has wrapper structure: { data: {...}, isSuccessful: true, ... }
    const apiData = res.data?.data || res.data;

    // Store full API response in localStorage
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apiData));
      dispatchStorageEvent(STORAGE_KEY, apiData);
    } catch (err) {
      console.warn('Failed to save settings to localStorage:', err);
    }

    // Map new API response fields to old field names for backward compatibility
    return {
      app_name: apiData?.applicationName || '',
      app_logo: getPublicUploadLogo() || getStaticLogoUrl(), // Static logo URL from env - not read from API
      phone_number_1: apiData?.phoneNumber1 || '',
      phone_number_2: apiData?.phoneNumber2 || '',
      app_url: apiData?.webSiteAddress || '',
      whatsapp_number: apiData?.whatsAppNumber || '',
      instagram_id: apiData?.instagramAccount || '',
      telegram_number: apiData?.telegramNumber || '',
      address: apiData?.address || '',
      pending_order_rejection_seconds: apiData?.rejectTime?.toString() || '',
      admin_sms_phone: apiData?.adminPhoneNumber || '',
      trading_start_time: apiData?.startTimeTrade || '',
      manager_message: apiData?.managerMessage || '',
      startTimeTrade : apiData?.startTimeTrade,
      lafzIsEnable: apiData?.lafzIsEnable === true,
    };
  } catch (err) {
    // If API call fails, try to read from localStorage
    try {
      const cached = localStorage.getItem(STORAGE_KEY);
      if (cached) {
        const cachedData = JSON.parse(cached);
        console.warn('API call failed, using cached settings:', err);
        return {
          app_name: cachedData?.applicationName || '',
          app_logo: getPublicUploadLogo() || getStaticLogoUrl(), // Static logo URL from env - not read from API
          phone_number_1: cachedData?.phoneNumber1 || '',
          phone_number_2: cachedData?.phoneNumber2 || '',
          app_url: cachedData?.webSiteAddress || '',
          whatsapp_number: cachedData?.whatsAppNumber || '',
          instagram_id: cachedData?.instagramAccount || '',
          telegram_number: cachedData?.telegramNumber || '',
          address: cachedData?.address || '',
          pending_order_rejection_seconds: cachedData?.rejectTime?.toString() || '',
          admin_sms_phone: cachedData?.adminPhoneNumber || '',
          trading_start_time: cachedData?.startTimeTrade || '',
          manager_message: cachedData?.managerMessage || '',
          startTimeTrade: cachedData?.startTimeTrade,
          lafzIsEnable: cachedData?.lafzIsEnable === true,
        };
      }
    } catch (cacheErr) {
      console.warn('Failed to read cached settings:', cacheErr);
    }
    // If no cached data and API fails, throw the error
    throw err;
  }
}

/**
 * Update a specific setting (admin only)
 */
export async function updateSetting(key, value) {
  const res = await api.put(`/settings/${key}`, { value });
  return res.data;
}

/**
 * Update multiple settings at once (admin only)
 * Maps old field names to new API format and sends to /trading/person/info
 */
export async function updateAppSettings(settings) {
  // Map old field names to new API field names
  // Note: logo is not sent to API - it's static and managed separately
  const mappedSettings = {
    applicationName: settings.app_name || '',
    // logo field removed - using static logo instead
    phoneNumber1: settings.phone_number_1 || '',
    phoneNumber2: settings.phone_number_2 || '',
    webSiteAddress: settings.app_url || '',
    whatsAppNumber: settings.whatsapp_number || '',
    instagramAccount: settings.instagram_id || '',
    telegramNumber: settings.telegram_number || '',
    address: settings.address || '',
    rejectTime: settings.pending_order_rejection_seconds
      ? parseInt(settings.pending_order_rejection_seconds, 10)
      : 0,
    adminPhoneNumber: settings.admin_sms_phone || '',
    startTimeTrade: settings.trading_start_time || '',
    managerMessage: settings.manager_message || '',
  };

  const res = await api.put('/trading/person/info', mappedSettings);
  return res.data;
}

/**
 * Get sidebar menus for current user (based on their role)
 */
export async function getSidebarMenus() {
  const res = await api.get('/settings/sidebar-menus');
  return res.data;
}

/**
 * Get full sidebar ACL configuration (admin only - for managing role permissions)
 */
export async function getSidebarACL() {
  const res = await api.get('/settings/sidebar-acl');
  return res.data;
}

/**
 * Get admin menus for permission management
 */
export async function getAdminMenus() {
  const acl = await getSidebarACL();
  return acl.admin || [];
}

/**
 * Send trading start SMS to all active users and personnel (admin only)
 * Message: سامانه هوشمند #SHOP_NAME# فعال شد
 */
export async function sendTradingStartSMS() {
  const res = await api.post('/settings/send-trading-start-sms');
  return res.data;
}

